// src/tests/ChildTest/NonwordRepetition/TrialNonwords.js
import React, {Component} from 'react';
import strings from 'strings';
import listen from 'assets/listen.png';
import {updateStepData} from 'api';
import {OBJECT_IMAGE_MAP, SHAPE_IMAGE_MAP} from './assets';

import {
	getChildTrialPack,
	noDistractionsSounds,
	nnDistractionsSounds,
} from './assets';
import './styles.scss';
import AudioPlayer from 'components/AudioPlayer';

// Helper to get random int inclusive
const randInt = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;

class TrialNonwords extends Component {
	constructor(props) {
		super(props);
		const langCode = strings.getLanguage();
		const {testKeyType} = this.props;
		this.trial = getChildTrialPack(langCode, testKeyType);
		this.distractionVoiceOvers =
			langCode === 'nn' ? nnDistractionsSounds : noDistractionsSounds;
		this.state = {
			isInDelay: false, // true during 15s distraction window
			distraction: null, // { type, ...payload }
		};

		this.sayAgainTimeout = null;
	}

	componentDidMount() {
		this.initializeAudio();
	}

	componentWillUnmount() {
		this.stopAll();
		clearTimeout(this.sayAgainTimeout);
	}

	componentDidUpdate(prevProps) {
		// If step index changes (3 trial items), restart stimulus
		if (prevProps.currentStep !== this.props.currentStep) {
			this.startNewTask();
		}

		const prevSD = prevProps.stepData || {};
		const nextSD = this.props.stepData || {};

		// When admin has just coded the trial (answered toggles false -> true)
		if (nextSD.answered && !prevSD.answered) {
			this.playFeedback(nextSD.correct, nextSD.reason);
		}

		// When admin has just scored the *immediate* repetition (same flag as Test)
		if (nextSD.nonwordImmediateDone && !prevSD.nonwordImmediateDone) {
			this.scheduleDelayedCue();
		}
	}

	initializeAudio = () => {
		// Preload all relevant clips
		Object.values(this.trial).forEach((a) => a && a.load && a.load());
		// Small delay so everything is ready before we start
		setTimeout(() => this.startNewTask(), 500);
	};

	startNewTask = () => {
		this.stopAll();
		clearTimeout(this.sayAgainTimeout);

		// New trial item → clear flag & distraction (keeps Test + Trial in sync)
		updateStepData({nonwordImmediateDone: false});

		this.setState({
			isInDelay: false,
			distraction: null,
		});

		const {stimulus} = this.trial;
		if (!stimulus) return;

		// Play the trial stimulus immediately
		if (stimulus.play) {
			stimulus.play();
		}

		// IMPORTANT:
		// We do NOT schedule "sayAgain" here anymore.
		// That now happens after admin scores the immediate response
		// (via scheduleDelayedCue).
	};

	// ---------- distraction generation (same logic as Test, but simpler sequence) ----------

	createRandomDistraction = () => {
		const {testKeyType} = this.props || {};

		const tk = String(testKeyType || '').toUpperCase();
		const isYA =
			tk.includes('Y/A') ||
			tk.includes('YOUTH') ||
			tk.includes('UNGDOM') ||
			tk.includes('ADULT') ||
			tk.includes('VOKSEN') ||
			tk.includes('YA');

		const types = [
			'CLAP_COUNT',
			'COLOR_CIRCLES',
			'NUMBER_LIST',
			'SAME_COLOR',
			'NAME_ANIMALS',
			'NAME_OBJECTS',
			'SIZE_ORDER',
			'SHAPE_ANALOGY',
		];

		const type = 'SHAPE_ANALOGY';

		switch (type) {
			case 'CLAP_COUNT': {
				const n = randInt(5, 12);
				return {type, n};
			}

			case 'COLOR_CIRCLES': {
				const colors = [
					'red',
					'green',
					'blue',
					'yellow',
					'purple',
					'orange',
				];
				const count = randInt(4, 6);
				const chosen = [];
				while (chosen.length < count) {
					const c = colors[randInt(0, colors.length - 1)];
					if (!chosen.includes(c)) chosen.push(c);
				}
				return {type, colors: chosen};
			}

			case 'NUMBER_LIST': {
				const min = isYA ? 100 : 1;
				const max = isYA ? 1000 : 100;
				const howMany = isYA ? randInt(4, 6) : randInt(3, 5);

				const numbers = [];
				while (numbers.length < howMany) {
					const num = randInt(min, max);
					if (!numbers.includes(num)) numbers.push(num);
				}
				return {type, numbers};
			}

			case 'SAME_COLOR': {
				const colors = [
					'red',
					'green',
					'blue',
					'yellow',
					'brown',
					'orange',
				];
				const color = colors[randInt(0, colors.length - 1)];
				return {type, color};
			}

			case 'NAME_ANIMALS':
				return {type};

			case 'NAME_OBJECTS': {
				const allLabels = Object.keys(OBJECT_IMAGE_MAP);
				const minCount = isYA ? 6 : 4;
				const maxCount = isYA ? 8 : 6;
				const count = randInt(minCount, maxCount);

				const items = [];
				while (
					items.length < count &&
					items.length < allLabels.length
				) {
					const idx = randInt(0, allLabels.length - 1);
					const label = allLabels[idx];
					if (!items.includes(label)) items.push(label);
				}
				return {type, items};
			}

			case 'SIZE_ORDER': {
				const allLabels = Object.keys(OBJECT_IMAGE_MAP);
				const count = randInt(3, Math.min(6, allLabels.length));

				const items = [];
				while (items.length < count) {
					const idx = randInt(0, allLabels.length - 1);
					const label = allLabels[idx];
					if (!items.find((x) => x.label === label)) {
						items.push({
							label,
							sizeRank: randInt(1, 5),
						});
					}
				}
				items.sort((a, b) => b.sizeRank - a.sizeRank);
				return {type, items};
			}

			case 'SHAPE_ANALOGY': {
				const shapes = Object.keys(SHAPE_IMAGE_MAP); // ['triangle','square','circle']
				const shape = shapes[randInt(0, shapes.length - 1)];
				return {type, shape};
			}

			default:
				return {type: 'NAME_ANIMALS'};
		}
	};

	scheduleDelayedCue = () => {
		clearTimeout(this.sayAgainTimeout);

		const distraction = this.createRandomDistraction();

		this.setState({
			isInDelay: true,
			distraction,
		});

		// Run distraction for 15 seconds, then play trial "say it again"
		this.sayAgainTimeout = setTimeout(() => {
			this.setState({isInDelay: false, distraction: null});

			const {sayAgain} = this.trial;
			if (sayAgain && sayAgain.play) {
				sayAgain.play();
			}
		}, 15000);
	};

	// ---------- feedback & stop ----------

	playFeedback = (isCorrect, reason) => {
		// Stop stimuli + any previous feedback
		this.stopAll();
		clearTimeout(this.sayAgainTimeout);

		const {correct, wrong, noAnswer10s, wrong10s} = this.trial;

		let clip = null;

		if (isCorrect === true) {
			clip = correct || wrong;
		} else {
			if (reason === 'noAnswer10s' && noAnswer10s) {
				clip = noAnswer10s;
			} else if (reason === 'wrong10s' && wrong10s) {
				clip = wrong10s;
			} else {
				clip = wrong;
			}
		}

		if (!clip || !clip.play) {
			setTimeout(() => updateStepData({feedbackDone: true}), 800);
			return;
		}

		clip.once('end', () => {
			setTimeout(() => updateStepData({feedbackDone: true}), 400);
		});
		clip.play();
	};

	stopAll = () => {
		const stop = (s) => {
			if (!s) return;
			if (s.stop) s.stop();
			else if (s.pause) s.pause();
		};

		Object.values(this.trial).forEach(stop);
	};

	// ---------- render helpers (reuse same CSS as Test) ----------

	renderDistraction = () => {
		const {distraction} = this.state;
		if (!distraction) return null;

		switch (distraction.type) {
			case 'CLAP_COUNT':
				return (
					<div className="nw-distraction">
						<p className="nw-distraction__instruction">
							<AudioPlayer
								autoplay
								inline
								src={this.distractionVoiceOvers.distraction1}
								styles={{display: 'none'}}
							/>
						</p>
						<div className="nw-distraction__big-number">
							{distraction.n}
						</div>
					</div>
				);

			case 'COLOR_CIRCLES':
				return (
					<div className="nw-distraction">
						<p className="nw-distraction__instruction">
							<AudioPlayer
								autoplay
								inline
								src={this.distractionVoiceOvers.distraction2}
								styles={{display: 'none'}}
							/>
						</p>
						<div className="nw-distraction__circles">
							{distraction.colors.map((c) => (
								<div
									key={c}
									className="nw-distraction__circle"
									style={{backgroundColor: c}}
								/>
							))}
						</div>
					</div>
				);

			case 'NUMBER_LIST':
				return (
					<div className="nw-distraction">
						<p className="nw-distraction__instruction">
							<AudioPlayer
								autoplay
								inline
								src={this.distractionVoiceOvers.distraction3}
								styles={{display: 'none'}}
							/>
						</p>
						<div className="nw-distraction__numbers">
							{distraction.numbers.map((n) => (
								<span
									key={n}
									className="nw-distraction__number"
								>
									{n}
								</span>
							))}
						</div>
					</div>
				);

			case 'SAME_COLOR':
				return (
					<div className="nw-distraction">
						<p className="nw-distraction__instruction">
							<AudioPlayer
								autoplay
								inline
								src={this.distractionVoiceOvers.distraction6}
								styles={{display: 'none'}}
							/>
						</p>
						<div
							className="nw-distraction__color-block"
							style={{backgroundColor: distraction.color}}
						/>
					</div>
				);

			case 'NAME_OBJECTS':
				return (
					<div className="nw-distraction">
						<p className="nw-distraction__instruction">
							<AudioPlayer
								autoplay
								inline
								src={this.distractionVoiceOvers.distraction7}
								styles={{display: 'none'}}
							/>
						</p>
						<div className="nw-distraction__objects nw-distraction__objects--images">
							{distraction.items.map((label, idx) => {
								const src = OBJECT_IMAGE_MAP[label];
								if (!src) return null; // should not happen anymore
								return (
									<div
										key={`${label}-${idx}`}
										className="nw-distraction__object-imageWrapper"
									>
										<img
											src={src}
											alt={label}
											className="nw-distraction__object-image"
										/>
									</div>
								);
							})}
						</div>
					</div>
				);

			case 'SIZE_ORDER':
				return (
					<div className="nw-distraction">
						<p className="nw-distraction__instruction">
							<AudioPlayer
								autoplay
								inline
								src={this.distractionVoiceOvers.distraction8}
								styles={{display: 'none'}}
							/>
						</p>
						<div className="nw-distraction__objects nw-distraction__objects--images">
							{distraction.items.map((item, idx) => {
								const src = OBJECT_IMAGE_MAP[item.label];
								if (!src) return null;
								const scale = 1 + item.sizeRank * 0.12;

								return (
									<div
										key={`${item.label}-${idx}`}
										className="nw-distraction__object-imageWrapper nw-distraction__object-imageWrapper--sized"
										style={{transform: `scale(${scale})`}}
									>
										<img
											src={src}
											alt={item.label}
											className="nw-distraction__object-image"
										/>
									</div>
								);
							})}
						</div>
					</div>
				);

			case 'SHAPE_ANALOGY':
				return (
					<div className="nw-distraction">
						<p className="nw-distraction__instruction">
							<AudioPlayer
								autoplay
								inline
								src={this.distractionVoiceOvers.distraction4}
								styles={{display: 'none'}}
							/>
						</p>
						<div className="nw-distraction__shape-wrapper">
							{(() => {
								const src = SHAPE_IMAGE_MAP[distraction.shape];
								if (!src) return null;
								return (
									<img
										src={src}
										alt={distraction.shape}
										className="nw-distraction__shape-image"
									/>
								);
							})()}
						</div>
					</div>
				);

			case 'NAME_ANIMALS':
			default:
				return (
					<div className="nw-distraction">
						<p className="nw-distraction__instruction">
							<AudioPlayer
								autoplay
								inline
								src={this.distractionVoiceOvers.distraction5}
								styles={{display: 'none'}}
							/>
							{strings.nonword_distr_animals ||
								'Nevn fire dyr så raskt du klarer.'}
						</p>
					</div>
				);
		}
	};

	render() {
		const {isInDelay} = this.state;
	
		return (
			<div className="NonwordRepetition">
				<img className="listen-image" src={listen} alt="" />

				{!isInDelay && (
					<p>
						{strings.nonword_trial_listen_repeat ||
							strings.sm_trial_listen_repeat}
					</p>
				)}

				{isInDelay && this.renderDistraction()}
			</div>
		);
	}
}

export default TrialNonwords;
